import type { GameStore } from '../simulation/GameStore';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { api, getUsername, isLoggedIn, logout, getServerUrl, setServerUrl } from '../net/api';
import { getLang } from './i18n';

/**
 * Online account panel (DOM overlay): register / login, cloud save slots,
 * leaderboard, and viewing another player's lots. Fully optional — every action
 * catches "offline" and shows a message, so the game still works with no server
 * (e.g. in the static artifact). Requires the backend in `server/` when run
 * locally.
 */
export class AccountPanel {
  private readonly root: HTMLElement;
  private readonly store: GameStore;
  private readonly onLoadPlay?: () => void;
  private overlay?: HTMLElement;

  constructor(root: HTMLElement, store: GameStore, onLoadPlay?: () => void) {
    this.root = root;
    this.store = store;
    this.onLoadPlay = onLoadPlay;
  }

  private th(): boolean {
    return getLang() === 'th';
  }
  private tr(en: string, th: string): string {
    return this.th() ? th : en;
  }

  open(): void {
    this.injectStyles();
    const overlay = document.createElement('div');
    overlay.className = 'pt-acc-backdrop ui-interactive';
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });
    const panel = document.createElement('div');
    panel.className = 'pt-acc';
    overlay.appendChild(panel);
    this.root.appendChild(overlay);
    this.overlay = overlay;
    this.render(panel);
  }

  close(): void {
    this.overlay?.remove();
    this.overlay = undefined;
  }

  private render(panel: HTMLElement): void {
    panel.innerHTML = '';
    const close = document.createElement('button');
    close.className = 'pt-acc-close';
    close.textContent = '✕';
    close.onclick = () => this.close();
    panel.appendChild(close);

    const title = document.createElement('h2');
    title.textContent = this.tr('🌐 Online', '🌐 ออนไลน์');
    panel.appendChild(title);

    if (!isLoggedIn()) {
      this.renderAuth(panel);
    } else {
      this.renderAccount(panel);
    }
  }

  private msg(panel: HTMLElement, text: string, ok = false): void {
    let el = panel.querySelector<HTMLElement>('.pt-acc-msg');
    if (!el) {
      el = document.createElement('div');
      el.className = 'pt-acc-msg';
      panel.appendChild(el);
    }
    el.textContent = text;
    el.style.color = ok ? '#a5d6a7' : '#ff8a80';
  }

  private offlineText(): string {
    return this.tr(
      'Offline — start the backend (server/) and set the server URL.',
      'ออฟไลน์ — ต้องรัน backend (server/) และตั้ง URL เซิร์ฟเวอร์'
    );
  }

  private renderAuth(panel: HTMLElement): void {
    const user = document.createElement('input');
    user.placeholder = this.tr('username', 'ชื่อผู้ใช้');
    user.className = 'pt-acc-input';
    const pass = document.createElement('input');
    pass.type = 'password';
    pass.placeholder = this.tr('password', 'รหัสผ่าน');
    pass.className = 'pt-acc-input';

    const server = document.createElement('input');
    server.value = getServerUrl();
    server.className = 'pt-acc-input';
    server.title = 'Server URL';
    server.placeholder = this.tr(
      'Server URL (leave blank — same server)',
      'URL เซิร์ฟเวอร์ (เว้นว่างไว้ — ใช้เซิร์ฟเวอร์เดียวกัน)'
    );

    const rowBtns = document.createElement('div');
    rowBtns.className = 'pt-acc-row';
    const loginBtn = this.button(this.tr('Log in', 'เข้าสู่ระบบ'), async () => {
      setServerUrl(server.value);
      try {
        await api.login(user.value.trim(), pass.value);
        this.render(panel);
      } catch (e) {
        this.msg(panel, this.errText(e));
      }
    });
    const regBtn = this.button(this.tr('Register', 'สมัคร'), async () => {
      setServerUrl(server.value);
      try {
        await api.register(user.value.trim(), pass.value);
        this.render(panel);
      } catch (e) {
        this.msg(panel, this.errText(e));
      }
    });
    rowBtns.append(loginBtn, regBtn);

    const hint = document.createElement('p');
    hint.className = 'pt-acc-hint';
    hint.textContent = this.tr(
      'Accounts let you keep multiple cloud saves and see other players. Needs the local backend.',
      'บัญชีช่วยเก็บเซฟบนคลาวด์หลายอัน และดูผู้เล่นอื่น — ต้องรัน backend บนเครื่อง'
    );

    panel.append(user, pass, server, rowBtns, hint);
    this.renderLeaderboard(panel); // browsing works without login
  }

  private errText(e: unknown): string {
    const m = e instanceof Error ? e.message : String(e);
    return m === 'offline' ? this.offlineText() : m;
  }

  private renderAccount(panel: HTMLElement): void {
    const greet = document.createElement('div');
    greet.className = 'pt-acc-greet';
    greet.textContent = this.tr(`Signed in as ${getUsername()}`, `เข้าสู่ระบบ: ${getUsername()}`);
    const out = this.button(this.tr('Log out', 'ออกจากระบบ'), () => {
      logout();
      this.render(panel);
    });
    const head = document.createElement('div');
    head.className = 'pt-acc-row';
    head.append(greet, out);
    panel.appendChild(head);

    const slotsTitle = document.createElement('h3');
    slotsTitle.textContent = this.tr('Cloud saves', 'เซฟบนคลาวด์');
    panel.appendChild(slotsTitle);

    const slotsBox = document.createElement('div');
    slotsBox.className = 'pt-acc-slots';
    panel.appendChild(slotsBox);
    this.loadSlots(panel, slotsBox);

    this.renderLeaderboard(panel);
  }

  private async loadSlots(panel: HTMLElement, box: HTMLElement): Promise<void> {
    box.textContent = '…';
    let saves: Awaited<ReturnType<typeof api.listSaves>> = [];
    try {
      saves = await api.listSaves();
    } catch (e) {
      box.textContent = this.errText(e);
      return;
    }
    box.innerHTML = '';
    for (let slot = 0; slot < 5; slot++) {
      const info = saves.find((s) => s.slot === slot);
      const row = document.createElement('div');
      row.className = 'pt-acc-slot';
      const label = document.createElement('span');
      label.textContent = info
        ? `#${slot} ${info.companyName} • ${formatCurrencyCompact(info.companyValue)}`
        : this.tr(`#${slot} (empty)`, `#${slot} (ว่าง)`);
      const save = this.button(this.tr('Save', 'บันทึก'), async () => {
        try {
          await api.saveSlot(slot, `Slot ${slot}`, this.store.getState());
          this.msg(panel, this.tr('Saved to cloud', 'บันทึกขึ้นคลาวด์แล้ว'), true);
          this.loadSlots(panel, box);
        } catch (e) {
          this.msg(panel, this.errText(e));
        }
      });
      const load = this.button(this.tr('Load', 'โหลด'), async () => {
        try {
          const { data } = await api.loadSlot(slot);
          if (this.store.loadFromData(data)) {
            this.close();
            this.onLoadPlay?.();
          } else {
            this.msg(panel, this.tr('Invalid save', 'เซฟไม่ถูกต้อง'));
          }
        } catch (e) {
          this.msg(panel, this.errText(e));
        }
      });
      if (!info) load.disabled = true;
      row.append(label, save, load);
      box.appendChild(row);
    }
  }

  private async renderLeaderboard(panel: HTMLElement): Promise<void> {
    const h = document.createElement('h3');
    h.textContent = this.tr('🏆 Leaderboard', '🏆 อันดับผู้เล่น');
    panel.appendChild(h);
    const box = document.createElement('div');
    box.className = 'pt-acc-board';
    box.textContent = '…';
    panel.appendChild(box);
    try {
      const rows = await api.leaderboard();
      box.innerHTML = '';
      if (!Array.isArray(rows) || rows.length === 0) {
        box.textContent = this.tr('No players yet.', 'ยังไม่มีผู้เล่น');
        return;
      }
      rows.forEach((r, i) => {
        const row = document.createElement('div');
        row.className = 'pt-acc-boardrow';
        row.textContent = `${i + 1}. ${r.username} — ${r.companyName} (${formatCurrencyCompact(r.companyValue)})`;
        row.title = this.tr('View lots', 'ดูลานจอด');
        row.onclick = () => this.viewLots(panel, r.username);
        box.appendChild(row);
      });
    } catch (e) {
      box.textContent = this.errText(e);
    }
  }

  private async viewLots(panel: HTMLElement, username: string): Promise<void> {
    try {
      const data = await api.viewLots(username);
      const lines = data.lots
        .map((l) => `• ${l.name} — ฿${l.price}/hr, rep ${l.reputation}, rev ${formatCurrencyCompact(l.revenue)}`)
        .join('\n');
      this.msg(
        panel,
        `${username}: ${data.companyName} (${formatCurrencyCompact(data.companyValue)})\n${lines}`,
        true
      );
    } catch (e) {
      this.msg(panel, this.errText(e));
    }
  }

  private button(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = 'pt-acc-btn';
    b.textContent = label;
    b.onclick = onClick;
    return b;
  }

  private injectStyles(): void {
    const ID = 'pt-acc-styles';
    if (document.getElementById(ID)) return;
    const s = document.createElement('style');
    s.id = ID;
    s.textContent = `
      .pt-acc-backdrop { position:absolute; inset:0; background:rgba(0,0,0,0.6);
        display:flex; align-items:center; justify-content:center; z-index:40; }
      .pt-acc { position:relative; width:min(520px,92vw); max-height:88vh; overflow:auto;
        background:#141c2b; border:1px solid #5c6bc0; border-radius:12px; padding:20px;
        color:#eceff1; font-family:'Segoe UI',Tahoma,sans-serif; }
      .pt-acc h2 { margin:0 0 12px; color:#ffd54f; font-size:20px; }
      .pt-acc h3 { margin:16px 0 6px; color:#90a4ae; font-size:14px; }
      .pt-acc-close { position:absolute; top:12px; right:12px; width:30px; height:30px;
        background:#263238; color:#eceff1; border:1px solid #455a64; border-radius:6px; cursor:pointer; }
      .pt-acc-input { display:block; width:100%; margin:6px 0; padding:9px 10px; border-radius:6px;
        background:#0f1622; color:#eceff1; border:1px solid #37474f; font-size:14px; }
      .pt-acc-row { display:flex; gap:8px; align-items:center; margin:8px 0; }
      .pt-acc-btn { padding:8px 14px; background:#1e88e5; color:#fff; border:none; border-radius:6px;
        cursor:pointer; font-size:14px; font-weight:600; }
      .pt-acc-btn:disabled { background:#37474f; color:#78909c; cursor:default; }
      .pt-acc-hint, .pt-acc-msg { font-size:12px; color:#90a4ae; margin-top:8px; white-space:pre-line; }
      .pt-acc-greet { flex:1; font-weight:600; }
      .pt-acc-slot { display:flex; gap:8px; align-items:center; margin:5px 0; font-size:13px; }
      .pt-acc-slot span { flex:1; }
      .pt-acc-board { display:flex; flex-direction:column; gap:4px; }
      .pt-acc-boardrow { padding:6px 8px; background:#1a2333; border-radius:6px; cursor:pointer; font-size:13px; }
      .pt-acc-boardrow:hover { background:#243447; }
    `;
    document.head.appendChild(s);
  }
}
